import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithPopup } from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import './IntakeForm.css';
import { LanguageContext } from '../App';
import { useAuth } from '../context/AuthContext';
import { auth, db, googleProvider } from '../firebase';
import { getPostAuthRedirect } from '../utils/guestIntake';
import {
  buildInstallPromptUrl,
  shouldShowPostRegistrationInstallPrompt,
} from '../utils/installPrompt';
import { translations } from '../utils/translations';

function Register() {
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const t = translations[language] || translations.en;
  const { register, setIntakeComplete } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createInitialProfile = async (user) => {
    if (!db || !user) {
      return;
    }

    await setDoc(
      doc(db, 'users', user.uid),
      {
        userId: user.uid,
        email: user.email || '',
        profileComplete: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const handlePostAuthRedirect = () => {
    const nextPath = getPostAuthRedirect('/dashboard');

    if (shouldShowPostRegistrationInstallPrompt()) {
      window.location.replace(buildInstallPromptUrl({ continuePath: nextPath, language }));
      return;
    }

    navigate(nextPath, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (password !== confirmPassword) {
      setError(t.register.errors.passwordMismatch);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const credentials = await register(email, password);
      await createInitialProfile(credentials.user);
      setIntakeComplete(false);
      handlePostAuthRedirect();
    } catch (registerError) {
      setError(registerError.message || t.register.errors.unableToCreateAccount);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleRegister = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      if (!auth) {
        throw new Error('Firebase Auth is not configured.');
      }

      const credentials = await signInWithPopup(auth, googleProvider);
      await createInitialProfile(credentials.user);
      setIntakeComplete(false);
      handlePostAuthRedirect();
    } catch (googleError) {
      setError(googleError.message || t.register.errors.unableToContinueWithGoogle);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-content">
        <div className="form-section">
          <button className="back-button" onClick={() => navigate('/')}>
            {t.register.back}
          </button>
          <h2>{t.register.title}</h2>
          <p className="form-intro">{t.register.sub}</p>

          <form onSubmit={handleSubmit} className="intake-form">
            <div className="form-group">
              <label htmlFor="register-email">{t.register.email}</label>
              <input
                id="register-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.register.emailPlaceholder}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="register-password">{t.register.password}</label>
              <input
                id="register-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.register.passwordPlaceholder}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="register-password-confirm">{t.register.confirm}</label>
              <input
                id="register-password-confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t.register.confirmPasswordPlaceholder}
                required
              />
            </div>

            {error && <div className="submit-message error-message-box">{error}</div>}

            <button type="submit" className="btn btn-primary btn-large" disabled={isSubmitting}>
              {isSubmitting ? t.register.submitLoading : t.register.registerBtn}
            </button>

            <button
              type="button"
              className="btn btn-secondary btn-large"
              onClick={handleGoogleRegister}
              disabled={isSubmitting}
            >
              {t.register.google}
            </button>

            <button
              type="button"
              className="btn btn-secondary btn-large"
              onClick={() => navigate('/login')}
            >
              {t.register.existing}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Register;
